// ---------------------------------------------------------------------------
// AppLockContext — React context owning the AppLock instance lifecycle
// ---------------------------------------------------------------------------
//
// Implements the deferred-encryption pattern with `ui: false` (headless),
// as described in INTEGRATION.md Step 2b. The provider owns creation and
// teardown of the AppLock instance and exposes lock state via a hook.
// ---------------------------------------------------------------------------

import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { AppLock, type AppLockInstance } from '@ascend/applock';
import { setLockStore } from '@/utils/secureStorage';

const DEFERRED_KEY = 'app-lock-deferred';

export type LockDecision = 'pending' | 'skipped' | 'enabled';

// AppLock's meta-keys that must be cleared when transitioning states.
// These persist in localStorage and cause stale `isConfigured = true` if
// not removed.
const APPLOCK_META_KEYS = ['applock_hash', 'applock_salt', 'applock_config', 'applock_version'];

function clearAppLockMetaKeys(): void {
  for (const key of APPLOCK_META_KEYS) {
    localStorage.removeItem(key);
  }
}

function getStoredDecision(): LockDecision {
  const stored = localStorage.getItem(DEFERRED_KEY);
  if (stored === 'skipped') return 'skipped';
  if (stored === 'enabled') return 'enabled';
  return 'pending';
}

function createLock(): AppLockInstance {
  return AppLock.init({
    mode: 'encrypted',
    appRootSelector: '#root',
    lockOnHide: false,
    idleTimeout: 900,
    ui: false, // ALWAYS false — we render our own React screens
  });
}

interface LockState {
  lock: AppLockInstance | null;
  isLocked: boolean;
  isUnlocked: boolean;
  isConfigured: boolean;
  decision: LockDecision;
}

interface AppLockContextValue extends LockState {
  isEnabled: boolean;
  skipEncryption: () => void;
  enableEncryption: () => void;
}

const AppLockContext = createContext<AppLockContextValue | null>(null);

// CRITICAL: isConfigured must ALWAYS initialize as false.
// The real value comes from the lock instance after it's created.
// Initializing it to anything else (e.g. decision !== 'pending') causes
// the LockScreen to appear on first load instead of the SetupScreen.
function getInitialState(): LockState {
  const decision = getStoredDecision();
  return {
    lock: null,
    isLocked: false,
    isUnlocked: decision === 'skipped',
    isConfigured: false, // ALWAYS false — updated by lock instance
    decision,
  };
}

export function AppLockProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<LockState>(getInitialState);
  const instanceRef = useRef<AppLockInstance | null>(null);

  // CRITICAL: Clear stale AppLock meta-keys when skipping.
  // Without this, a subsequent enableEncryption() would create a lock
  // that finds the old hash and reports isConfigured=true.
  const skipEncryption = useCallback(() => {
    localStorage.setItem(DEFERRED_KEY, 'skipped');
    clearAppLockMetaKeys();
    if (instanceRef.current) {
      instanceRef.current.destroy();
      instanceRef.current = null;
    }
    // Route storage back to plain localStorage
    setLockStore(null);
    setState({
      lock: null,
      isLocked: false,
      isUnlocked: true,
      isConfigured: false,
      decision: 'skipped',
    });
  }, []);

  // CRITICAL: Clear stale AppLock meta-keys before enabling.
  // Old hashes from prior runs make the new lock instance think a password
  // was already set, showing LockScreen instead of SetupScreen.
  const enableEncryption = useCallback(() => {
    localStorage.setItem(DEFERRED_KEY, 'enabled');
    clearAppLockMetaKeys();
    // No lock instance yet — storage stays plain until the instance is
    // created in the effect below.
    setLockStore(null);
    setState((prev) => ({
      ...prev,
      lock: null,
      decision: 'enabled',
      isUnlocked: false,
      isConfigured: false,
    }));
  }, []);

  useEffect(() => {
    if (state.decision !== 'enabled') return;

    const instance = createLock();
    instanceRef.current = instance;

    // Wire the encrypted storage adapter into the secure storage layer so
    // all app persistence goes through encryption while unlocked.
    setLockStore(instance.store);

    const onLock = () => {
      setState((prev) => ({ ...prev, isLocked: true, isUnlocked: false }));
    };
    const onUnlock = () => {
      setState((prev) => ({
        ...prev,
        isLocked: false,
        isUnlocked: true,
        isConfigured: true,
      }));
    };
    const onPasswordClear = () => {
      setState((prev) => ({ ...prev, isConfigured: false }));
    };
    const onSetup = () => {
      setState((prev) => ({ ...prev, isConfigured: true }));
    };

    instance.on('lock', onLock);
    instance.on('unlock', onUnlock);
    instance.on('password:clear', onPasswordClear);
    instance.on('setup', onSetup);

    // CRITICAL: The 'uninitialized' state means no password has been set.
    // Treat it as unlocked so the SetupScreen shows (not the LockScreen).
    setState((prev) => ({
      ...prev,
      lock: instance,
      isLocked: instance.isLocked,
      isUnlocked: instance.state === 'unlocked' || instance.state === 'uninitialized',
      isConfigured: instance.isConfigured,
    }));

    return () => {
      setLockStore(null);
      instance.destroy();
      instanceRef.current = null;
    };
  }, [state.decision]);

  // Loading state while lock instance is being created
  if (state.decision === 'enabled' && !state.lock) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--color-bg)]">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <AppLockContext.Provider
      value={{
        ...state,
        isEnabled: state.decision === 'enabled' && state.lock !== null,
        skipEncryption,
        enableEncryption,
      }}
    >
      {children}
    </AppLockContext.Provider>
  );
}

export function useAppLock(): AppLockContextValue {
  const ctx = useContext(AppLockContext);
  if (!ctx) throw new Error('useAppLock must be used within AppLockProvider');
  return ctx;
}
